import {debounce} from 'lodash'
import {useEffect, useRef, useState} from 'react'
import {Link} from 'react-router-dom'
import Paginator from '../../../custom/Paginator'
import Loader from '../../../custom/loader/Loader'
import {Dropdown, DropdownButton} from 'react-bootstrap'
import {useTranslation} from 'react-i18next'
import {showConfirmation, showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import {useAppDispatch} from '../../../../redux/hooks'
import {deleteRole, getRoles} from '../../../../redux/Slice/userSlice'
import {encryptId} from '../../../../_metronic/helpers/Encrypt'
import NoData from '_metronic/helpers/NoData'
import {useAuth} from '../core/Auth'
import CustomSelect from 'app/custom/CustomSelect'
import {RecordsNo} from '_metronic/helpers/jsonData'

const SORT_ASC = 'asc'
const SORT_DESC = 'desc'
const RoleDataTable = (props: any) => {
  const [data, setData] = useState([])
  const [perPage, setPerPage] = useState(20)
  const [sortColumn, setSortColumn] = useState(props.columns[0])
  const [sortOrder, setSortOrder] = useState('asc')
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const {t} = useTranslation()

  const dispatch = useAppDispatch()

  const {currentUser, hasPermission} = useAuth()

  const currentRole = (roleName: string) => {
    return currentUser?.roles.some((role: string) => role === `${roleName}`)
  }

  const handleSort = (column: any) => {
    if (column === sortColumn) {
      sortOrder === SORT_ASC ? setSortOrder(SORT_DESC) : setSortOrder(SORT_ASC)
    } else {
      setSortColumn(column)
      setSortOrder(SORT_ASC)
    }
  }
  const handleSearch = useRef(
    debounce((query) => {
      setLoading(true)
      setSearch(query)
      setCurrentPage(1)
      setSortOrder(SORT_ASC)
      setSortColumn(props.columns[0])
    }, 500)
  ).current

  // Global params for pagination
  const params = {
    search,
    sort_field: sortColumn,
    sort_order: sortOrder,
    per_page: perPage,
    page: currentPage,
  }

  const listData = () => {
    dispatch(getRoles({params})).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setData(res.payload.data)
        setPagination(res.payload.meta)
        setLoading(false)
      }
    })
  }

  useEffect(() => {
    listData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, perPage, search, sortColumn, sortOrder])

  const handleDelete = (role_id: any) => {
    showConfirmation(
      t('MESSAGES.CONFIRMATION'),
      t('GLOBAL.YES'),
      t('GLOBAL.NO'),
      () => {
        setLoading(true)
        dispatch(deleteRole(role_id)).then((res) => {
          if (res.meta.requestStatus === 'fulfilled') {
            showSuccessMessage(t('MESSAGES.DELETED'), () => {
              listData()
            })
          }
        })
      },
      () => showErrorMessage(t('MESSAGES.SERVER_ERROR'))
    )
  }

  return (
    <div>
      {/* Search per page starts */}
      <div>
        <h5>{t('GLOBAL.SEARCH')}</h5>
        <div className='row mb-5'>
          <div className='col-6'>
            <input
              type='search'
              placeholder={t('LABEL.ROLE')}
              className='form-control form-control-sm'
              onChange={(e) => handleSearch(e.target.value)}
            />
          </div>
          <div className='col-6'>
            <CustomSelect
              className='input'
              onChange={(e: any) => {
                setLoading(true)
                setCurrentPage(1)
                setPerPage(e.id)
              }}
              options={RecordsNo}
              value={perPage}
              placeholderText={t('GLOBAL.RECORD_PER_PAGE')}
            />
          </div>
        </div>
      </div>
      {/* Search per page ends  */}
      <div className='tableFixHead'>
        <table className='table table-bordered table-hover table-checkable data-table gy-4 gs-5'>
          <thead className='table-secondary'>
            <tr>
              {props.headers.map((header: any) => {
                return (
                  <th key={header.headerName} onClick={(e) => handleSort(header.sort)}>
                    {header.headerName.replace('_', ' ')}
                    {header.sort === sortColumn ? (
                      <span>
                        {sortOrder === SORT_ASC ? (
                          <i className='ms-1 fa fa-arrow-up' aria-hidden='true'></i>
                        ) : (
                          <i className='ms-1 fa fa-arrow-down' aria-hidden='true'></i>
                        )}
                      </span>
                    ) : null}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && !loading ? <NoData Colspan={props.columns.length + 1} /> : ''}

            {!loading ? (
              data.map((role: any) => {
                return (
                  <tr key={role.id} className='flex-wrap'>
                    <td className='align-middle'>{role.name}</td>
                    <td>
                      {role.permissions.map((p: any) => (
                        <span key={p.id}>
                          <span className='badge badge-primary my-1 mx-1'>{p.name}</span>
                        </span>
                      ))}
                    </td>
                    <td className='align-middle'>
                      <DropdownButton size='sm' title=''>
                        {hasPermission('role-update') && (
                          <Dropdown.Item
                            as={Link}
                            className='text-end'
                            to={`/role-edit/${encryptId(role.id)}`}
                          >
                            <i className='fas fa-edit text-warning'></i>
                            <span className='btn btn-sm fw-bolder'>{t('BUTTONS.EDIT')}</span>
                          </Dropdown.Item>
                        )}
                        {hasPermission('role-delete') && (
                          <Dropdown.Item
                            as='button'
                            onClick={() => handleDelete(role.id)}
                            disabled={currentRole(role.name)}
                            className='text-end'
                          >
                            <i className='fas fa-trash text-danger'></i>
                            <span className='btn btn-sm fw-bolder'>{t('BUTTONS.DELETE')}</span>
                          </Dropdown.Item>
                        )}
                      </DropdownButton>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={props.columns.length + 1}>
                  <div className='d-flex justify-content-center'>{loading && <Loader />}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {data.length > 0 && !loading ? (
        <div className='mt-2' style={{direction: 'ltr'}}>
          <Paginator
            pagination={pagination}
            pageChanged={(page: number) => setCurrentPage(page)}
            totalItems={data.length}
          />
        </div>
      ) : null}
    </div>
  )
}

export default RoleDataTable
